import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  Form,
  Input,
  InputNumber,
  DatePicker,
  Switch,
  Button,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Modal,
  message
} from 'antd'
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import PageLoader from '../../../components/Layouts/PageLoader'
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  dutyPromotionsUrl,
  dutyPromotionDetailUrl
} from '../../../config'
import { formatMoney } from '../../Analytics/utils'
import { promotionStatusTag } from './statusColors'

// Create/edit a promotion. Discount applies to the SERVICE FEE; dealers are excluded unless the
// applies-to-dealers toggle is on. Edit mode also shows redemption performance.
const PromotionForm = props => {
  const editingId = props.match.params.id || null
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [promo, setPromo] = useState(null)

  useEffect(() => {
    if (!editingId) return
    const load = async () => {
      const res = await getRequest(dutyPromotionDetailUrl(editingId), 'promotion', setLoading)
      if (res.success) {
        setPromo(res.data)
        form.setFieldsValue({
          name: res.data.name,
          description: res.data.description,
          discountPercent: Number(res.data.discountPercent),
          maxDiscountAmount: res.data.maxDiscountAmount
            ? Number(res.data.maxDiscountAmount)
            : null,
          startAt: res.data.startAt ? moment(res.data.startAt) : null,
          endAt: res.data.endAt ? moment(res.data.endAt) : null,
          appliesToDealers: res.data.appliesToDealers,
          isActive: res.data.isActive
        })
      }
    }
    load()
    // eslint-disable-next-line
  }, [editingId])

  const onFinish = async values => {
    const payload = {
      name: values.name,
      description: values.description || '',
      discountPercent: values.discountPercent,
      maxDiscountAmount: values.maxDiscountAmount || null,
      startAt: values.startAt.toISOString(),
      endAt: values.endAt.toISOString(),
      appliesToDealers: !!values.appliesToDealers,
      isActive: values.isActive !== false
    }
    const res = editingId
      ? await patchRequest(dutyPromotionDetailUrl(editingId), payload, 'promotion', setSaving)
      : await postRequest(dutyPromotionsUrl, payload, 'promotion', setSaving)
    if (res.success) {
      message.success(editingId ? 'Promotion updated' : 'Promotion created')
      props.history.push('/duty-assistant/promotions')
    } else if (res.error && res.error.response && res.error.response.data) {
      // Surface API field errors inline.
      const data = res.error.response.data
      const fields = Object.keys(data).map(k => ({
        name: k,
        errors: [Array.isArray(data[k]) ? data[k][0] : String(data[k])]
      }))
      form.setFields(fields)
    }
  }

  const confirmDelete = () => {
    Modal.confirm({
      title: 'Delete this promotion?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Deleting keeps past redemption amounts on their cars, but removes the promotion. Consider turning it Off instead to keep its history.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await deleteRequest(
          dutyPromotionDetailUrl(editingId),
          'promotion',
          setSaving
        )
        if (res.success) {
          message.success('Promotion deleted')
          props.history.push('/duty-assistant/promotions')
        }
      }
    })
  }

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <Space style={{ marginBottom: 8 }} wrap>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => props.history.push('/duty-assistant/promotions')}
            >
              Back
            </Button>
            {editingId && promo ? promotionStatusTag(promo) : null}
          </Space>
          <h3 style={{ marginBottom: 4 }}>
            {editingId ? 'Edit promotion' : 'New promotion'}
          </h3>
          <p style={{ color: '#888', marginBottom: 16 }}>
            The discount applies to the duty <strong>service fee</strong> (not the government
            duty). Dealers keep their usual price unless you turn on “applies to dealers”.
          </p>

          {editingId && promo ? (
            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title="Redemptions" value={promo.redemptionCount || 0} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title="Paid" value={promo.paidRedemptionCount || 0} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Discount given"
                    value={formatMoney(promo.totalDiscount || 0, 'GHS')}
                  />
                </Card>
              </Col>
            </Row>
          ) : null}

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ appliesToDealers: false, isActive: true }}
            style={{ maxWidth: 640 }}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Give the promotion a name.' }]}
            >
              <Input placeholder="e.g. July Discount" />
            </Form.Item>
            <Form.Item name="description" label="Description (optional)">
              <Input.TextArea rows={2} placeholder="Internal note about this promotion" />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="discountPercent"
                  label="Discount %"
                  rules={[
                    { required: true, message: 'Enter a percentage.' },
                    {
                      type: 'number',
                      min: 0.01,
                      max: 90,
                      message: 'Between 0 and 90.'
                    }
                  ]}
                >
                  <InputNumber min={0} max={90} style={{ width: '100%' }} addonAfter="%" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="maxDiscountAmount"
                  label="Max discount cap (GHS, optional)"
                  rules={[{ type: 'number', min: 0, message: 'Cannot be negative.' }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} addonBefore="GHS" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="startAt"
                  label="Starts"
                  rules={[{ required: true, message: 'Pick a start.' }]}
                >
                  <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="endAt"
                  label="Ends"
                  dependencies={['startAt']}
                  rules={[
                    { required: true, message: 'Pick an end.' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const start = getFieldValue('startAt')
                        if (!value || !start || value.isAfter(start)) return Promise.resolve()
                        return Promise.reject(new Error('End must be after start.'))
                      }
                    })
                  ]}
                >
                  <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="appliesToDealers"
              label="Applies to dealers"
              valuePropName="checked"
              extra="Off = dealers keep their negotiated price. On = dealers also get this promo."
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="isActive"
              label="Active"
              valuePropName="checked"
              extra="Master on/off switch, independent of the dates."
            >
              <Switch />
            </Form.Item>

            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingId ? 'Save changes' : 'Create promotion'}
              </Button>
              {editingId ? (
                <Button danger icon={<DeleteOutlined />} onClick={confirmDelete}>
                  Delete
                </Button>
              ) : null}
            </Space>
          </Form>
        </div>
      </div>
    </PageLoader>
  )
}

PromotionForm.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object
}

export default PromotionForm
